import React from 'react';
import { Newspaper, Podcast, ExternalLink, Quote } from 'lucide-react';
import { Button } from './ui/button';
import { mediaFeatures } from '../data/mock';
import useScrollAnimation from '../hooks/useScrollAnimation';

const Media = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.2);
  const [cardsRef, cardsVisible] = useScrollAnimation(0.1);

  const getIcon = (type) => {
    switch (type) {
      case 'article':
        return Newspaper;
      case 'podcast':
        return Podcast;
      default:
        return Newspaper;
    }
  };

  return (
    <section id="media" className="py-24 bg-slate-900">
      <div className="max-w-7xl mx-auto px-6">
        <div 
          ref={headerRef}
          className={`text-center mb-16 animate-on-scroll ${headerVisible ? 'visible' : ''}`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 border border-teal-500/20 mb-6">
            <Newspaper className="w-4 h-4 text-teal-400" />
            <span className="text-teal-400 text-sm font-medium">Media & Publications</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Featured In
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Industry recognition and thought leadership in educational technology
          </p>
        </div>

        <div ref={cardsRef} className="grid md:grid-cols-2 gap-8">
          {mediaFeatures.map((feature, index) => {
            const Icon = getIcon(feature.type);
            return (
              <div
                key={feature.id}
                className={`group p-6 md:p-8 rounded-2xl bg-slate-950 border border-slate-800 hover:border-teal-500/30 transition-all duration-300 flex flex-col animate-scale-in stagger-${index + 1} ${cardsVisible ? 'visible' : ''}`}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl bg-teal-500/10 text-teal-400 group-hover:bg-teal-500/20 transition-colors">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <span className="text-teal-400 text-sm font-medium">{feature.publication}</span>
                      <p className="text-slate-500 text-xs">{feature.date}</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-slate-800 text-slate-400 text-xs capitalize">
                    {feature.type}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-teal-400 transition-colors">
                  {feature.title}
                </h3>

                <p className="text-slate-400 text-sm mb-4 flex-grow">
                  {feature.description}
                </p>

                {feature.quote && (
                  <div className="relative p-4 rounded-xl bg-slate-900 border border-slate-800 mb-4">
                    <Quote className="absolute top-2 left-2 w-4 h-4 text-teal-500/30" />
                    <p className="text-slate-300 text-sm italic pl-4">
                      "{feature.quote}"
                    </p>
                  </div>
                )}

                <a href={feature.url} target="_blank" rel="noopener noreferrer">
                  <Button
                    variant="outline"
                    className="w-full border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white gap-2 mt-auto"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {feature.type === 'podcast' ? 'Listen Now' : 'Read Article'}
                  </Button>
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Media;
